// PurchaseOrderPage.js - Complete with Global Components
// VERSION: 3.2 - Using Global Components including StatusBadge, ViewOrderModal, TrackingModal, DeleteModal
// Updated: Code generation pattern PV00126 (2 digits year suffix)
// Updated: Replaced ConfirmDialog with DeleteModal

import React, { useState, useMemo, useEffect, useCallback } from 'react';
import Select from 'react-select';
import useFirestore from '../../hooks/useFirestore';
import usePagePermissions from '../../hooks/usePagePermissions';
import useAuth from '../../hooks/useAuth';
import DeleteModal from '../../components/common/DeleteModal/DeleteModal'; // Changed from ConfirmDialog
import Toast from '../../components/common/Toast/Toast';
import SearchBar from '../../components/common/SearchBar/SearchBar';
import Pagination from '../../components/common/Pagination/Pagination';
import TableSkeleton from '../../components/common/TableSkeleton/TableSkeleton';
import EmptyState from '../../components/common/EmptyState/EmptyState';
import PageHeader from '../../components/common/PageHeader/PageHeader';
import StatusBadge from '../../components/common/StatusBadge/StatusBadge';
import ViewOrderModal from '../../components/common/ViewOrderModal/ViewOrderModal';
import TrackingModal from '../../components/common/TrackingModal/TrackingModal';
import styles from './Orders.module.css';

// React Icons - Only imported what's actually used
import {
    FiX,
    FiSave,
    FiEdit,
    FiTrash2,
    FiArrowLeft,
    FiArrowRight,
    FiRefreshCw,
    FiCheck,
    FiAlertCircle,
    FiInfo,
    FiFileText,
    FiSearch,
    FiPackage,
    FiClipboard,
    FiTag,
    FiMapPin,
    FiList,
    FiEye,
    FiPlus,
    FiTruck,
    FiDollarSign,
    FiTrendingUp,
    FiCheckCircle,
    FiXCircle,
    FiClock,
    FiSettings,
    FiSend
} from 'react-icons/fi';
import {
    MdBusiness,
    MdCancel
} from 'react-icons/md';
import { FaDollarSign } from 'react-icons/fa';

// ============================================
// 1. CONSTANTES E DEFINIÇÕES
// ============================================
const PURCHASE_ORDERS_COLLECTION = 'purchase-orders';
const SUPPLIERS_COLLECTION = 'suppliers';
const RAW_MATERIALS_COLLECTION = 'raw-materials';
const BILLING_CONDITIONS_COLLECTION = 'parameters/data/billing-conditions';
const CARRIERS_COLLECTION = 'transportadoras';
const NCM_COLLECTION = 'parameters/data/ncm';
const ITEMS_PER_PAGE = 10;

// Status do Pedido
const ORDER_STATUS = {
    PENDING: 'pending',
    APPROVED: 'approved',
    COMPLETED: 'completed',
    CANCELLED: 'cancelled',
    REJECTED: 'rejected'
};

const ORDER_STATUS_LABELS = {
    [ORDER_STATUS.PENDING]: { label: 'Pendente', className: 'statusBadgePending' },
    [ORDER_STATUS.APPROVED]: { label: 'Aprovado', className: 'statusBadgeApproved' },
    [ORDER_STATUS.COMPLETED]: { label: 'Concluído', className: 'statusBadgeCompleted' },
    [ORDER_STATUS.CANCELLED]: { label: 'Cancelado', className: 'statusBadgeCancelled' },
    [ORDER_STATUS.REJECTED]: { label: 'Rejeitado', className: 'statusBadgeRejected' }
};

// Tipos de Entrega
const DELIVERY_TYPES = [
    { value: 'transportadora', label: 'Na transportadora' },
    { value: 'retirar', label: 'À retirar' },
    { value: 'nosso_endereco', label: 'Nosso endereço' },
    { value: 'triangular', label: 'Triangular' }
];
const DEFAULT_DELIVERY_TYPE = DELIVERY_TYPES[0].value;

// Frete por conta
const FREIGHT_BY_OPTIONS = [
    { value: 'emitente', label: 'Emitente' },
    { value: 'destinatario', label: 'Destinatário' }
];
const DEFAULT_FREIGHT_BY = FREIGHT_BY_OPTIONS[0].value;

// Dólar opções
const DOLLAR_OPTIONS = ['Dolar Faturamento', 'Dolar Médio', 'Dolar Dia', 'Dolar Negociado'];

// ============================================
// 2. SUB-COMPONENTES
// ============================================

// NCM Summary Row Component
const NCMSummaryRowPurchase = React.memo(({ ncmCode, ncmConfig }) => {
    if (!ncmConfig) return null;

    return (
        <tr className={styles.ncmSummaryRow}>
            <td colSpan="7" style={{ padding: '8px 20px' }}>
                <div>
                    <span><strong>NCM:</strong> {ncmCode}</span>
                    <span><strong>ST:</strong> {ncmConfig.situacaoTributaria || '00'}</span>
                    <span><strong>ICMS:</strong> {ncmConfig.aliquotaIcmsProprio || 0}%</span>
                    <span><strong>ICMS ST:</strong> {ncmConfig.aliquotaIcmsSTOutraUF || 0}%</span>
                    <span><strong>IPI:</strong> {ncmConfig.aliquotaIpiSaida || 0}%</span>
                    <span><strong>PIS:</strong> {ncmConfig.aliquotaPISSaida || 0}%</span>
                    <span><strong>COFINS:</strong> {ncmConfig.aliquotaCofinsSaida || 0}%</span>
                    {ncmConfig.aliquotaII > 0 && <span><strong>II:</strong> {ncmConfig.aliquotaII}%</span>}
                </div>
            </td>
        </tr>
    );
});

NCMSummaryRowPurchase.displayName = 'NCMSummaryRowPurchase';

// ============================================
// 3. MAIN COMPONENT
// ============================================
const PurchaseOrderPage = () => {
    const can = usePagePermissions('pedido_de_compra');

    // ============================================
    // 3.1 ESTADOS PRINCIPAIS
    // ============================================
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentStep, setCurrentStep] = useState(1);
    const [editingOrder, setEditingOrder] = useState(null);
    const [viewingOrder, setViewingOrder] = useState(null);
    const [trackingOrder, setTrackingOrder] = useState(null);
    const [earlyPaymentOrder, setEarlyPaymentOrder] = useState(null);
    const [formStatus, setFormStatus] = useState({ message: '', type: '' });
    const [formLoading, setFormLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [toasts, setToasts] = useState([]);
    const [deleteModal, setDeleteModal] = useState(null); // Changed from deleteConfirm
    const [trackingUpdateConfirm, setTrackingUpdateConfirm] = useState(null);

    const TOTAL_STEPS = 4;

    // Estados do Formulário
    const [orderCode, setOrderCode] = useState('');
    const [selectedSupplierId, setSelectedSupplierId] = useState('');
    const [supplierAddress, setSupplierAddress] = useState('');
    const [supplierAccount, setSupplierAccount] = useState('');
    const [orderDate, setOrderDate] = useState('');
    const [discountPercent, setDiscountPercent] = useState('');
    const [dollarType, setDollarType] = useState(DOLLAR_OPTIONS[3]);
    const [dollarValue, setDollarValue] = useState('1.0000');
    const [dollarFaturamento, setDollarFaturamento] = useState('');
    const [dollarMedio, setDollarMedio] = useState('');
    const [dollarDia, setDollarDia] = useState('');
    const [dollarNegociado, setDollarNegociado] = useState('1.0000');

    // Estados Fiscais
    const [ipiValue, setIpiValue] = useState('');
    const [icmsValue, setIcmsValue] = useState('');
    const [icmsStValue, setIcmsStValue] = useState('');
    const [pisValue, setPisValue] = useState('');
    const [cofinsValue, setCofinsValue] = useState('');
    const [iiValue, setIiValue] = useState('');

    const [freightValue, setFreightValue] = useState('');
    const [deliveryType, setDeliveryType] = useState(DEFAULT_DELIVERY_TYPE);
    const [freightBy, setFreightBy] = useState(DEFAULT_FREIGHT_BY);
    const [selectedCarrierId, setSelectedCarrierId] = useState('');
    const [selectedBillingConditionId, setSelectedBillingConditionId] = useState('');
    const [paymentDeadline, setPaymentDeadline] = useState('');
    const [commercialNotes, setCommercialNotes] = useState('');
    const [sendToReceiving, setSendToReceiving] = useState(false);
    const [sendEmail, setSendEmail] = useState(false);

    // Itens do pedido
    const [orderItems, setOrderItems] = useState([]);
    const [selectedMaterialId, setSelectedMaterialId] = useState('');

    // ============================================
    // 3.2 HOOKS DE DADOS
    // ============================================
    const { currentUser, loading: authLoading } = useAuth();

    const {
        data: orders,
        loading: ordersLoading,
        addDocument,
        updateDocument,
        deleteDocument,
        refreshData: refreshOrders
    } = useFirestore(PURCHASE_ORDERS_COLLECTION, 'createdAt', true);

    const { data: suppliers, loading: suppliersLoading } = useFirestore(SUPPLIERS_COLLECTION, 'razaoSocial');
    const { data: rawMaterials, loading: rawMaterialsLoading } = useFirestore(RAW_MATERIALS_COLLECTION, 'code');
    const { data: billingConditions, loading: billingConditionsLoading } = useFirestore(BILLING_CONDITIONS_COLLECTION, 'name');
    const { data: carriers, loading: carriersLoading } = useFirestore(CARRIERS_COLLECTION, 'razaoSocial');
    const { data: ncmList, loading: ncmListLoading } = useFirestore(NCM_COLLECTION);

    // Create a map for quick NCM lookup by ID
    const ncmMap = useMemo(() => {
        if (!ncmList) return new Map();
        const map = new Map();
        ncmList.forEach(ncm => {
            if (ncm.id) {
                map.set(ncm.id, ncm);
            }
        });
        return map;
    }, [ncmList]);

    // Responsável
    const responsible = useMemo(() => {
        if (currentUser) {
            return currentUser.nome || currentUser.email?.split('@')[0] || 'Usuário';
        }
        return 'Usuário';
    }, [currentUser]);

    // ============================================
    // 3.3 SHOW SKELETON
    // ============================================
    const showSkeleton = useMemo(() => {
        if (ordersLoading && (!orders || orders.length === 0)) return true;
        return false;
    }, [ordersLoading, orders]);

    // ============================================
    // 3.4 TOAST FUNCTIONS
    // ============================================
    const addToast = (message, type) => {
        const id = Date.now();
        setToasts(prev => [...prev, { id, message, type }]);
        setTimeout(() => {
            setToasts(prev => prev.filter(toast => toast.id !== id));
        }, 5000);
    };

    // ============================================
    // 3.5 FUNÇÕES AUXILIARES
    // ============================================

    /**
     * Gerar código no padrão PV00126 (2 dígitos finais = ano)
     * Exemplo: Ano 2026 = PV00126
     * 
     * Formato: PV + 3 dígitos sequenciais + 2 dígitos do ano
     * - PV: Prefixo fixo
     * - 001-999: Número sequencial por ano
     * - 26: Ano (2 últimos dígitos)
     */
    const generateOrderCode = useCallback(async () => {
        const currentYear = new Date().getFullYear();
        const yearSuffix = currentYear.toString().slice(-2); // Ex: 2026 -> "26"

        // Filtra pedidos do ano atual
        const thisYearOrders = (orders || []).filter(order => {
            if (!order.code) return false;
            // Verifica se o código termina com o ano atual (ex: "...26")
            return order.code.endsWith(yearSuffix);
        });

        // Se não houver pedidos no ano atual, começa com 001
        if (thisYearOrders.length === 0) {
            return `PV001${yearSuffix}`;
        }

        // Extrai o número sequencial dos códigos existentes
        let maxNumber = 0;
        thisYearOrders.forEach(order => {
            // Extrai os 3 dígitos após "PV" (ex: "PV00126" -> "001")
            const match = order.code.match(/^PV(\d{3})\d{2}$/);
            if (match) {
                const num = parseInt(match[1], 10);
                if (!isNaN(num) && num > maxNumber) {
                    maxNumber = num;
                }
            }
        });

        // Incrementa o número
        const nextNumber = (maxNumber + 1).toString().padStart(3, '0');
        return `PV${nextNumber}${yearSuffix}`;
    }, [orders]);

    // Buscar informações do fornecedor selecionado
    const selectedSupplier = useMemo(() => {
        if (!selectedSupplierId || !suppliers) return null;
        return suppliers.find(s => s.id === selectedSupplierId);
    }, [selectedSupplierId, suppliers]);

    // Buscar condição de faturamento selecionada
    const selectedBillingCondition = useMemo(() => {
        if (!selectedBillingConditionId || !billingConditions) return null;
        return billingConditions.find(b => b.id === selectedBillingConditionId);
    }, [selectedBillingConditionId, billingConditions]);

    // Buscar transportadora selecionada
    const selectedCarrier = useMemo(() => {
        if (!selectedCarrierId || !carriers) return null;
        return carriers.find(c => c.id === selectedCarrierId);
    }, [selectedCarrierId, carriers]);

    // Atualizar prazo de faturamento quando a condição mudar
    useEffect(() => {
        if (selectedBillingCondition) {
            setPaymentDeadline(selectedBillingCondition.days || '');
        } else {
            setPaymentDeadline('');
        }
    }, [selectedBillingCondition]);

    // Atualizar endereço e conta contábil quando fornecedor mudar
    useEffect(() => {
        if (selectedSupplier) {
            const firstAddress = selectedSupplier.addresses && selectedSupplier.addresses.length > 0
                ? selectedSupplier.addresses[0]
                : null;

            if (firstAddress) {
                const addressStr = `${firstAddress.endereco || ''}, ${firstAddress.numero || ''}${firstAddress.complemento ? `, ${firstAddress.complemento}` : ''} - ${firstAddress.bairro || ''} - ${firstAddress.cidade || ''}/${firstAddress.uf || ''} - CEP: ${firstAddress.cep || ''}`;
                setSupplierAddress(addressStr);
            } else {
                setSupplierAddress('');
            }

            setSupplierAccount(selectedSupplier.contaContabil || '');
        } else {
            setSupplierAddress('');
            setSupplierAccount('');
        }
    }, [selectedSupplier]);

    // Inicializar data do pedido com data atual
    useEffect(() => {
        if (isModalOpen && !editingOrder) {
            const today = new Date();
            const formattedDate = today.toLocaleDateString('pt-BR');
            setOrderDate(formattedDate);
        }
    }, [isModalOpen, editingOrder]);

    // Gerar código quando o modal abrir para novo pedido
    useEffect(() => {
        if (isModalOpen && !editingOrder) {
            const generateCode = async () => {
                const code = await generateOrderCode();
                setOrderCode(code);
            };
            generateCode();
        }
    }, [isModalOpen, editingOrder, generateOrderCode]);

    // Calcular totais dos itens
    const calculateItemTotal = (quantity, unitPrice) => {
        const qty = parseFloat(quantity) || 0;
        const price = parseFloat(unitPrice) || 0;
        return qty * price;
    };

    // Calcular valor total dos itens
    const itemsSubtotal = useMemo(() => {
        return orderItems.reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0);
    }, [orderItems]);

    // Calcular valores líquidos
    const calculateValues = useMemo(() => {
        const subtotal = itemsSubtotal;
        const discount = (parseFloat(discountPercent) || 0);
        const discountAmount = subtotal * (discount / 100);
        const subtotalWithDiscount = subtotal - discountAmount;

        const ipi = (parseFloat(ipiValue) || 0);
        const icms = (parseFloat(icmsValue) || 0);
        const icmsSt = (parseFloat(icmsStValue) || 0);
        const pis = (parseFloat(pisValue) || 0);
        const cofins = (parseFloat(cofinsValue) || 0);
        const ii = (parseFloat(iiValue) || 0);
        const freight = (parseFloat(freightValue) || 0);

        const total = subtotalWithDiscount + ipi + icms + icmsSt + pis + cofins + ii + freight;

        return {
            subtotal: subtotal.toFixed(2),
            discountAmount: discountAmount.toFixed(2),
            subtotalWithDiscount: subtotalWithDiscount.toFixed(2),
            ipi: ipi.toFixed(2),
            icms: icms.toFixed(2),
            icmsSt: icmsSt.toFixed(2),
            pis: pis.toFixed(2),
            cofins: cofins.toFixed(2),
            ii: ii.toFixed(2),
            freight: freight.toFixed(2),
            total: total.toFixed(2)
        };
    }, [itemsSubtotal, discountPercent, ipiValue, icmsValue, icmsStValue, pisValue, cofinsValue, iiValue, freightValue]);

    // Valor Net em USD
    const valorNetUsd = useMemo(() => {
        const total = parseFloat(calculateValues.total) || 0;
        const dollar = parseFloat(dollarValue) || 1;
        return (total / dollar).toFixed(5);
    }, [calculateValues.total, dollarValue]);

    // Opções de matérias-primas filtradas por fornecedor
    const availableMaterials = useMemo(() => {
        if (!rawMaterials || !selectedSupplierId) return [];

        return rawMaterials.filter(material => {
            const isSupplied = material.suppliers && Array.isArray(material.suppliers) &&
                material.suppliers.some(s => s.supplierId === selectedSupplierId);
            return isSupplied;
        });
    }, [rawMaterials, selectedSupplierId]);

    const materialOptions = useMemo(() => {
        return availableMaterials.map(material => {
            const ncmInfo = material.ncm ? ncmMap.get(material.ncm) : null;
            const ncmCodeDisplay = ncmInfo?.ncmCode || material.ncmCode || '';

            return {
                value: material.id,
                label: `[${material.code}] ${material.commercialName} - ${material.inciName || ''}${ncmCodeDisplay ? ` (NCM: ${ncmCodeDisplay})` : ''}`,
                material: material,
                ncmId: material.ncm || null
            };
        });
    }, [availableMaterials, ncmMap]);

    // Opções de condições de faturamento
    const billingConditionOptions = useMemo(() => {
        if (!billingConditions) return [];
        return billingConditions.map(condition => ({
            value: condition.id,
            label: `${condition.name} - ${condition.days}`,
            days: condition.days
        }));
    }, [billingConditions]);

    // Opções de transportadoras
    const carrierOptions = useMemo(() => {
        if (!carriers) return [];
        return carriers.map(carrier => ({
            value: carrier.id,
            label: carrier.razaoSocial,
            carrier: carrier
        }));
    }, [carriers]);

    // ============================================
    // 3.6 STEPPER NAVIGATION
    // ============================================
    const goToNextStep = () => {
        if (currentStep < TOTAL_STEPS) {
            // Validação por passo
            if (currentStep === 1 && !selectedSupplierId) {
                setFormStatus({ message: 'Selecione um fornecedor para continuar.', type: 'error' });
                return;
            }
            if (currentStep === 2 && orderItems.length === 0) {
                setFormStatus({ message: 'Adicione pelo menos um item ao pedido.', type: 'error' });
                return;
            }
            setCurrentStep(prev => prev + 1);
            setFormStatus({ message: '', type: '' });
        }
    };

    const goToPreviousStep = () => {
        if (currentStep > 1) {
            setCurrentStep(prev => prev - 1);
            setFormStatus({ message: '', type: '' });
        }
    };

    // ============================================
    // 3.7 HANDLERS DO FORMULÁRIO
    // ============================================

    const resetForm = () => {
        setEditingOrder(null);
        setIsModalOpen(false);
        setCurrentStep(1);
        setOrderCode('');
        setSelectedSupplierId('');
        setSupplierAddress('');
        setSupplierAccount('');
        setOrderDate('');
        setDiscountPercent('');
        setDollarType(DOLLAR_OPTIONS[3]);
        setDollarFaturamento('');
        setDollarMedio('');
        setDollarDia('');
        setDollarNegociado('1.0000');
        setDollarValue('1.0000');
        setIpiValue('');
        setIcmsValue('');
        setIcmsStValue('');
        setPisValue('');
        setCofinsValue('');
        setIiValue('');
        setFreightValue('');
        setDeliveryType(DEFAULT_DELIVERY_TYPE);
        setFreightBy(DEFAULT_FREIGHT_BY);
        setSelectedCarrierId('');
        setSelectedBillingConditionId('');
        setPaymentDeadline('');
        setCommercialNotes('');
        setSendToReceiving(false);
        setSendEmail(false);
        setOrderItems([]);
        setSelectedMaterialId('');
        setFormStatus({ message: '', type: '' });
    };

    // Atualizar valor do dólar baseado no tipo selecionado
    const handleDollarTypeChange = (type) => {
        setDollarType(type);
        switch (type) {
            case 'Dolar Faturamento':
                setDollarValue(dollarFaturamento || '0.0000');
                break;
            case 'Dolar Médio':
                setDollarValue(dollarMedio || '0.0000');
                break;
            case 'Dolar Dia':
                setDollarValue(dollarDia || '0.0000');
                break;
            case 'Dolar Negociado':
                setDollarValue(dollarNegociado || '1.0000');
                break;
            default:
                setDollarValue('1.0000');
        }
    };

    // Adicionar material ao pedido
    const handleAddMaterial = () => {
        if (!selectedMaterialId) return;

        const selectedOption = materialOptions.find(opt => opt.value === selectedMaterialId);
        if (!selectedOption) return;

        const material = selectedOption.material;
        const supplierInfo = material.suppliers?.find(s => s.supplierId === selectedSupplierId);

        const ncmConfig = material.ncm ? ncmMap.get(material.ncm) : null;

        if (ncmConfig) {
            // Apply NCM tax config
            if (ncmConfig.aliquotaIcmsProprio !== undefined && ncmConfig.aliquotaIcmsProprio > 0) {
                setIcmsValue(ncmConfig.aliquotaIcmsProprio.toString());
            }
            if (ncmConfig.aliquotaIcmsSTOutraUF !== undefined && ncmConfig.aliquotaIcmsSTOutraUF > 0) {
                setIcmsStValue(ncmConfig.aliquotaIcmsSTOutraUF.toString());
            }
            if (ncmConfig.aliquotaIpiEntrada !== undefined && ncmConfig.aliquotaIpiEntrada > 0) {
                setIpiValue(ncmConfig.aliquotaIpiEntrada.toString());
            } else if (ncmConfig.aliquotaIpiSaida !== undefined && ncmConfig.aliquotaIpiSaida > 0) {
                setIpiValue(ncmConfig.aliquotaIpiSaida.toString());
            }
            if (ncmConfig.aliquotaPISEntrada !== undefined && ncmConfig.aliquotaPISEntrada > 0) {
                setPisValue(ncmConfig.aliquotaPISEntrada.toString());
            }
            if (ncmConfig.aliquotaCofinsEntrada !== undefined && ncmConfig.aliquotaCofinsEntrada > 0) {
                setCofinsValue(ncmConfig.aliquotaCofinsEntrada.toString());
            }
            if (ncmConfig.aliquotaII !== undefined && ncmConfig.aliquotaII > 0) {
                setIiValue(ncmConfig.aliquotaII.toString());
            }

            const ncmCode = ncmConfig.ncmCode || '';
            addToast(`Configuração fiscal do NCM ${ncmCode} carregada automaticamente`, 'Info');
        } else if (material.ncm) {
            addToast(`NCM da matéria-prima não possui configuração fiscal cadastrada`, 'Warning');
        }

        const newItem = {
            id: crypto.randomUUID(),
            materialId: material.id,
            code: material.code,
            name: material.commercialName,
            inciName: material.inciName || '',
            quantity: '',
            unitPrice: supplierInfo?.valor?.toString() || '0',
            total: '0',
            supplierSpecifics: supplierInfo || {},
            ncmId: material.ncm || null,
            ncmCode: ncmConfig?.ncmCode || null
        };

        setOrderItems(prev => [...prev, newItem]);
        setSelectedMaterialId('');
    };

    // Remover item do pedido
    const handleRemoveItem = (itemId) => {
        setOrderItems(prev => prev.filter(item => item.id !== itemId));
    };

    // Atualizar campo de um item
    const handleUpdateItem = (itemId, field, value) => {
        setOrderItems(prev => prev.map(item => {
            if (item.id === itemId) {
                const updated = { ...item, [field]: value };
                if (field === 'quantity' || field === 'unitPrice') {
                    const total = calculateItemTotal(
                        field === 'quantity' ? value : item.quantity,
                        field === 'unitPrice' ? value : item.unitPrice
                    );
                    updated.total = total.toFixed(2);
                }
                return updated;
            }
            return item;
        }));
    };

    const handleNewClick = () => {
        resetForm();
        setIsModalOpen(true);
    };

    const handleEditClick = async (order) => {
        setEditingOrder(order);

        setOrderCode(order.code || '');
        setSelectedSupplierId(order.supplierId || '');
        setOrderDate(order.orderDate || '');
        setDiscountPercent(order.discountPercent?.toString() || '');
        setDollarType(order.dollarType || DOLLAR_OPTIONS[3]);
        setDollarFaturamento(order.dollarFaturamento?.toString() || '');
        setDollarMedio(order.dollarMedio?.toString() || '');
        setDollarDia(order.dollarDia?.toString() || '');
        setDollarNegociado(order.dollarNegociado?.toString() || '1.0000');
        setDollarValue(order.dollarValue?.toString() || '1.0000');
        setIpiValue(order.ipiValue?.toString() || '');
        setIcmsValue(order.icmsValue?.toString() || '');
        setIcmsStValue(order.icmsStValue?.toString() || '');
        setPisValue(order.pisValue?.toString() || '');
        setCofinsValue(order.cofinsValue?.toString() || '');
        setIiValue(order.iiValue?.toString() || '');
        setFreightValue(order.freightValue?.toString() || '');
        setDeliveryType(order.deliveryType || DEFAULT_DELIVERY_TYPE);
        setFreightBy(order.freightBy || DEFAULT_FREIGHT_BY);
        setSelectedCarrierId(order.selectedCarrierId || '');
        setSelectedBillingConditionId(order.selectedBillingConditionId || '');
        setPaymentDeadline(order.paymentDeadline || '');
        setCommercialNotes(order.commercialNotes || '');
        setSendToReceiving(order.sendToReceiving || false);
        setSendEmail(order.sendEmail || false);
        setOrderItems(order.items || []);

        setCurrentStep(1);
        setIsModalOpen(true);
    };

    const handleViewClick = (order) => {
        setViewingOrder(order);
    };

    const handleTrackClick = (order) => {
        setTrackingOrder(order);
    };

    const handleEarlyPaymentClick = (order) => {
        setEarlyPaymentOrder(order);
    };

    const handleEarlyPaymentConfirm = async (paymentData) => {
        addToast(`Pagamento antecipado do pedido ${paymentData.orderCode} registrado com sucesso!`, 'Success');
    };

    /**
     * Handle delete - Opens DeleteModal instead of ConfirmDialog
     */
    const handleDelete = (order) => {
        setDeleteModal({
            isOpen: true,
            order: order,
            title: 'Confirmar Exclusão',
            message: `Tem certeza que deseja excluir o pedido ${order.code}? Esta ação não pode ser desfeita.`,
            itemName: order.supplierName || 'Pedido',
            itemCode: order.code || '',
            confirmText: 'Excluir Pedido',
        });
    };

    /**
     * Handle delete confirm - Executes the deletion
     */
    const handleDeleteConfirm = async () => {
        if (!deleteModal || !deleteModal.order) return;
        const order = deleteModal.order;

        try {
            const result = await deleteDocument(order.id);
            if (result.success) {
                addToast('Pedido excluído com sucesso!', 'Success');
                if (refreshOrders) await refreshOrders();
            } else {
                addToast(`Erro ao excluir: ${result.error}`, 'Error');
            }
        } catch (error) {
            addToast('Ocorreu um erro inesperado.', 'Error');
        } finally {
            setDeleteModal(null);
        }
    };

    /**
     * Handle delete cancel
     */
    const handleDeleteCancel = () => {
        setDeleteModal(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (formLoading) return;
        setFormStatus({ message: '', type: '' });

        if (!selectedSupplierId) {
            setFormStatus({ message: 'Selecione um fornecedor.', type: 'error' });
            return;
        }

        if (orderItems.length === 0) {
            setFormStatus({ message: 'Adicione pelo menos um item ao pedido.', type: 'error' });
            return;
        }

        setFormLoading(true);

        const orderData = {
            code: orderCode,
            supplierId: selectedSupplierId,
            supplierName: selectedSupplier?.razaoSocial || '',
            supplierAddress,
            supplierAccount,
            orderDate,
            discountPercent: parseFloat(discountPercent) || 0,
            dollarType,
            dollarFaturamento: parseFloat(dollarFaturamento) || 0,
            dollarMedio: parseFloat(dollarMedio) || 0,
            dollarDia: parseFloat(dollarDia) || 0,
            dollarNegociado: parseFloat(dollarNegociado) || 0,
            dollarValue: parseFloat(dollarValue) || 1,
            ipiValue: parseFloat(ipiValue) || 0,
            icmsValue: parseFloat(icmsValue) || 0,
            icmsStValue: parseFloat(icmsStValue) || 0,
            pisValue: parseFloat(pisValue) || 0,
            cofinsValue: parseFloat(cofinsValue) || 0,
            iiValue: parseFloat(iiValue) || 0,
            freightValue: parseFloat(freightValue) || 0,
            deliveryType,
            freightBy,
            selectedCarrierId,
            selectedCarrierName: selectedCarrier?.razaoSocial || '',
            selectedBillingConditionId,
            selectedBillingConditionName: selectedBillingCondition?.name || '',
            paymentDeadline,
            commercialNotes,
            responsible,
            sendToReceiving,
            sendEmail,
            items: orderItems.map(item => ({
                ...item,
                quantity: parseFloat(item.quantity) || 0,
                unitPrice: parseFloat(item.unitPrice) || 0,
                total: parseFloat(item.total) || 0
            })),
            calculatedValues: {
                subtotal: parseFloat(calculateValues.subtotal),
                discountAmount: parseFloat(calculateValues.discountAmount),
                subtotalWithDiscount: parseFloat(calculateValues.subtotalWithDiscount),
                ipi: parseFloat(calculateValues.ipi),
                icms: parseFloat(calculateValues.icms),
                icmsSt: parseFloat(calculateValues.icmsSt),
                pis: parseFloat(calculateValues.pis),
                cofins: parseFloat(calculateValues.cofins),
                ii: parseFloat(calculateValues.ii),
                freight: parseFloat(calculateValues.freight),
                total: parseFloat(calculateValues.total),
                valorNetUsd: parseFloat(valorNetUsd)
            },
            updatedAt: new Date()
        };

        if (!editingOrder) {
            orderData.createdAt = new Date();
            orderData.status = ORDER_STATUS.PENDING;
        }

        try {
            let result;
            if (editingOrder) {
                result = await updateDocument(editingOrder.id, orderData);
                if (result.success) {
                    addToast('Pedido atualizado com sucesso!', 'Success');
                    setTimeout(() => resetForm(), 1500);
                } else {
                    setFormStatus({ message: `Erro: ${result.error}`, type: 'error' });
                }
            } else {
                result = await addDocument(orderData);
                if (result.success) {
                    addToast('Pedido criado com sucesso!', 'Success');
                    setTimeout(() => resetForm(), 1500);
                } else {
                    setFormStatus({ message: `Erro: ${result.error}`, type: 'error' });
                }
            }

            if (result.success && refreshOrders) {
                await refreshOrders();
            }
        } catch (error) {
            setFormStatus({ message: `Erro ao salvar: ${error.message}`, type: 'error' });
        } finally {
            setFormLoading(false);
        }
    };

    // ============================================
    // 3.8 FILTROS E PAGINAÇÃO
    // ============================================

    const filteredOrders = useMemo(() => {
        if (!orders) return [];
        const lowerSearch = searchTerm.toLowerCase();

        return orders.filter(order => {
            const matchesSearch = (order.code && order.code.toLowerCase().includes(lowerSearch)) ||
                (order.supplierName && order.supplierName.toLowerCase().includes(lowerSearch));
            return matchesSearch;
        }).sort((a, b) => {
            const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
            const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
            return dateB - dateA;
        });
    }, [orders, searchTerm]);

    const totalPages = Math.ceil(filteredOrders.length / ITEMS_PER_PAGE);
    const currentOrders = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        return filteredOrders.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    }, [filteredOrders, currentPage]);

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) setCurrentPage(newPage);
    };

    // Helper para obter classe de status - Now using StatusBadge component
    const getStatusClass = (status) => {
        const statusConfig = ORDER_STATUS_LABELS[status] || ORDER_STATUS_LABELS[ORDER_STATUS.PENDING];
        return statusConfig.className;
    };

    const getStatusLabel = (status) => {
        const statusConfig = ORDER_STATUS_LABELS[status] || ORDER_STATUS_LABELS[ORDER_STATUS.PENDING];
        return statusConfig.label;
    };

    // ============================================
    // 3.9 RENDERIZAÇÃO DO STEPPER
    // ============================================

    const renderStepperHeader = () => (
        <div className={styles.stepperHeader}>
            <div className={styles.stepperSteps}>
                {[
                    { step: 1, label: 'Fornecedor' },
                    { step: 2, label: 'Itens & Dólar' },
                    { step: 3, label: 'Fiscal & Entrega' },
                    { step: 4, label: 'Revisão' },
                ].map(({ step, label }) => (
                    <div
                        key={step}
                        className={`${styles.stepperStep} ${step === currentStep
                            ? styles.stepperStepActive
                            : step < currentStep
                                ? styles.stepperStepCompleted
                                : ''
                            }`}
                        onClick={() => {
                            if (step < currentStep) {
                                setCurrentStep(step);
                            }
                        }}
                        style={{ cursor: step < currentStep ? 'pointer' : 'default' }}
                    >
                        <div className={styles.stepperCircle}>
                            {step < currentStep ? <FiCheck size={14} /> : step}
                        </div>
                        <span className={styles.stepperLabel}>{label}</span>
                        {step < TOTAL_STEPS && (
                            <div
                                className={`${styles.stepperLine} ${step < currentStep ? styles.stepperLineCompleted : ''
                                    }`}
                            />
                        )}
                    </div>
                ))}
            </div>
        </div>
    );

    const renderStepperActions = () => (
        <div className={styles.stepperActions}>
            <div>
                {currentStep > 1 && (
                    <button
                        type="button"
                        onClick={goToPreviousStep}
                        className={styles.secondaryButton}
                        disabled={formLoading}
                    >
                        <FiArrowLeft size={16} /> Voltar
                    </button>
                )}
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
                {currentStep < TOTAL_STEPS ? (
                    <button
                        type="button"
                        onClick={goToNextStep}
                        className={styles.primaryButton}
                        disabled={formLoading}
                    >
                        Próximo <FiArrowRight size={16} />
                    </button>
                ) : (
                    <button
                        type="submit"
                        className={styles.primaryButton}
                        disabled={formLoading}
                    >
                        {formLoading ? (
                            <>
                                <FiRefreshCw size={18} className={styles.buttonSpinner} />
                                Salvando...
                            </>
                        ) : (
                            editingOrder ? <><FiSave size={18} /> Salvar Alterações</> : <><FiPlus size={18} /> Criar Pedido</>
                        )}
                    </button>
                )}
            </div>
        </div>
    );

    // ============================================
    // 3.10 RENDERIZAÇÃO DOS PASSOS
    // ============================================

    const renderStepSupplier = () => (
        <div className={styles.stepContent}>
            <div className={styles.formSection}>
                <div className={styles.sectionHeader}>
                    <div className={styles.sectionIcon}><MdBusiness size={18} /></div>
                    <h3 className={styles.sectionTitle}>Informações do Fornecedor</h3>
                    <span className={styles.sectionBadge}>Passo 1 de 4</span>
                </div>
                <div className={styles.formGrid}>
                    <div className={styles.inputGroup}>
                        <label>Código do Pedido</label>
                        <input
                            type="text"
                            value={orderCode}
                            className={styles.input}
                            disabled
                        />
                        <small className={styles.fieldHint}>Gerado automaticamente (PV00126)</small>
                    </div>
                    <div className={styles.inputGroup}>
                        <label>Data do Pedido</label>
                        <input
                            type="text"
                            value={orderDate}
                            onChange={(e) => setOrderDate(e.target.value)}
                            className={styles.input}
                            placeholder="dd/mm/aaaa"
                            disabled={formLoading}
                        />
                    </div>
                    <div className={styles.inputGroup} style={{ gridColumn: 'span 3' }}>
                        <label>Fornecedor <span className={styles.required}>*</span></label>
                        <select
                            value={selectedSupplierId}
                            onChange={(e) => setSelectedSupplierId(e.target.value)}
                            className={styles.select}
                            disabled={formLoading || suppliersLoading}
                            required
                        >
                            <option value="">Selecione um fornecedor...</option>
                            {suppliers && suppliers.map(supplier => (
                                <option key={supplier.id} value={supplier.id}>
                                    {supplier.codigo} - {supplier.razaoSocial}
                                </option>
                            ))}
                        </select>
                        {suppliersLoading && <small className={styles.fieldHint}>Carregando fornecedores...</small>}
                    </div>
                </div>

                {selectedSupplier && (
                    <div className={styles.infoBox} style={{ margin: '16px 20px 0 20px' }}>
                        <div style={{ flex: 1 }}>
                            <FiMapPin size={16} style={{ marginRight: '6px' }} />
                            <strong>📍 Endereço:</strong> {supplierAddress || 'Não informado'}
                        </div>
                        <div style={{ flex: 1 }}>
                            <FiDollarSign size={16} style={{ marginRight: '6px' }} />
                            <strong>💰 Conta Contábil:</strong> {supplierAccount || 'Não informada'}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );

    const renderStepItemsAndDollar = () => {
        const calculated = calculateValues;

        return (
            <div className={styles.stepContent}>
                {/* Seção de Itens */}
                <div className={styles.formSection}>
                    <div className={styles.sectionHeader}>
                        <div className={styles.sectionIcon}><FiPackage size={18} /></div>
                        <h3 className={styles.sectionTitle}>Itens do Pedido</h3>
                        <span className={styles.sectionBadge}>{orderItems.length} itens</span>
                    </div>
                    <div style={{ padding: '20px' }}>
                        <div className={styles.formGrid} style={{ marginBottom: '20px' }}>
                            <div className={styles.inputGroup} style={{ gridColumn: 'span 2' }}>
                                <label>Adicionar Matéria Prima / Produto</label>
                                <select
                                    value={selectedMaterialId}
                                    onChange={(e) => setSelectedMaterialId(e.target.value)}
                                    className={styles.select}
                                    disabled={formLoading || !selectedSupplierId}
                                >
                                    <option value="">Selecione um item...</option>
                                    {materialOptions.map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                                {!selectedSupplierId && (
                                    <small className={styles.errorHint}>
                                        <FiAlertCircle size={12} style={{ marginRight: '4px' }} />
                                        Selecione um fornecedor primeiro
                                    </small>
                                )}
                            </div>
                            <div className={styles.inputGroup} style={{ justifyContent: 'flex-end', display: 'flex', alignItems: 'flex-end' }}>
                                <button
                                    type="button"
                                    onClick={handleAddMaterial}
                                    className={styles.addButton}
                                    disabled={formLoading || !selectedMaterialId || !selectedSupplierId}
                                >
                                    <FiPlus size={18} /> Adicionar Item
                                </button>
                            </div>
                        </div>

                        <div className={styles.tableContainer}>
                            <table className={styles.table}>
                                <thead>
                                    <tr>
                                        <th style={{ width: '50px' }}>#</th>
                                        <th>Código</th>
                                        <th>Nome do Produto</th>
                                        <th style={{ width: '100px' }}>Qtde</th>
                                        <th style={{ width: '120px' }}>Preço unit.</th>
                                        <th style={{ width: '120px' }}>Total</th>
                                        <th style={{ width: '100px' }}>Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {orderItems.length === 0 ? (
                                        <tr>
                                            <td colSpan="7" className={styles.emptyTable}>
                                                Nenhum item adicionado ao pedido.
                                            </td>
                                        </tr>
                                    ) : (
                                        orderItems.flatMap((item, index) => {
                                            const ncmConfig = item.ncmId ? ncmMap.get(item.ncmId) : null;
                                            return [
                                                <tr key={item.id}>
                                                    <td style={{ textAlign: 'center' }}>{index + 1}</td>
                                                    <td>
                                                        <input
                                                            type="text"
                                                            value={item.code || ''}
                                                            onChange={(e) => handleUpdateItem(item.id, 'code', e.target.value)}
                                                            className={styles.input}
                                                            disabled={formLoading}
                                                            placeholder="Código"
                                                        />
                                                    </td>
                                                    <td>
                                                        <input
                                                            type="text"
                                                            value={item.name || ''}
                                                            onChange={(e) => handleUpdateItem(item.id, 'name', e.target.value)}
                                                            className={styles.input}
                                                            disabled={formLoading}
                                                            placeholder="Nome do produto"
                                                        />
                                                    </td>
                                                    <td style={{ width: '100px' }}>
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            value={item.quantity || ''}
                                                            onChange={(e) => handleUpdateItem(item.id, 'quantity', e.target.value)}
                                                            className={styles.input}
                                                            disabled={formLoading}
                                                            placeholder="Qtde"
                                                        />
                                                    </td>
                                                    <td style={{ width: '120px' }}>
                                                        <input
                                                            type="number"
                                                            step="0.00001"
                                                            value={item.unitPrice || ''}
                                                            onChange={(e) => handleUpdateItem(item.id, 'unitPrice', e.target.value)}
                                                            className={styles.input}
                                                            disabled={formLoading}
                                                            placeholder="Preço unit."
                                                        />
                                                    </td>
                                                    <td style={{ width: '120px' }}>
                                                        <input
                                                            type="text"
                                                            value={parseFloat(item.total || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                                            className={styles.input}
                                                            disabled
                                                            style={{ fontWeight: 'bold' }}
                                                        />
                                                    </td>
                                                    <td style={{ width: '100px', textAlign: 'center' }}>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleRemoveItem(item.id)}
                                                            className={`${styles.actionButton} ${styles.deleteButton}`}
                                                            disabled={formLoading}
                                                            title="Remover item"
                                                        >
                                                            <FiTrash2 size={14} />
                                                        </button>
                                                    </td>
                                                </tr>,
                                                ncmConfig && (
                                                    <NCMSummaryRowPurchase
                                                        key={`${item.id}-ncm`}
                                                        ncmCode={ncmConfig.ncmCode}
                                                        ncmConfig={ncmConfig}
                                                    />
                                                )
                                            ].filter(Boolean);
                                        })
                                    )}
                                </tbody>
                                <tfoot>
                                    <tr className={styles.tableFooterRow}>
                                        <td colSpan="5" className={styles.labelRight}>
                                            <strong>Subtotal:</strong>
                                        </td>
                                        <td className={styles.valueCell}>
                                            R$ {parseFloat(calculated.subtotal).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                        </td>
                                        <td></td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    </div>
                </div>

                {/* Seção de Dólar */}
                <div className={styles.formSection}>
                    <div className={styles.sectionHeader}>
                        <div className={styles.sectionIcon}><FaDollarSign size={18} /></div>
                        <h3 className={styles.sectionTitle}>Dólar</h3>
                    </div>
                    <div className={styles.formGrid}>
                        <div className={styles.inputGroup}>
                            <label>Tipo de Dólar</label>
                            <select
                                value={dollarType}
                                onChange={(e) => handleDollarTypeChange(e.target.value)}
                                className={styles.select}
                                disabled={formLoading}
                            >
                                {DOLLAR_OPTIONS.map(opt => (
                                    <option key={opt} value={opt}>{opt}</option>
                                ))}
                            </select>
                        </div>
                        <div className={styles.inputGroup}>
                            <label>Valor {dollarType}</label>
                            <input
                                type="number"
                                step="0.0001"
                                value={dollarValue}
                                onChange={(e) => setDollarValue(e.target.value)}
                                className={styles.input}
                                disabled={formLoading}
                            />
                        </div>
                    </div>
                    <div className={styles.infoBox} style={{ margin: '16px 20px 0 20px' }}>
                        <FiDollarSign size={16} style={{ marginRight: '6px' }} />
                        <strong>💵 Valor Net (USD):</strong> {valorNetUsd}
                    </div>
                </div>
            </div>
        );
    };

    const renderStepTaxAndDelivery = () => {
        const calculated = calculateValues;

        return (
            <div className={styles.stepContent}>
                {/* Valores Fiscais */}
                <div className={styles.formSection}>
                    <div className={styles.sectionHeader}>
                        <div className={styles.sectionIcon}><FiDollarSign size={18} /></div>
                        <h3 className={styles.sectionTitle}>Valores Fiscais</h3>
                        <span className={styles.sectionBadge}>Auto-preenchido pelo NCM</span>
                    </div>
                    <div className={styles.formGrid}>
                        <div className={styles.inputGroup}>
                            <label>IPI (R$)</label>
                            <input
                                type="number"
                                step="0.00001"
                                value={ipiValue}
                                onChange={(e) => setIpiValue(e.target.value)}
                                className={styles.input}
                                placeholder="0,00000"
                                disabled={formLoading}
                            />
                            <small className={styles.fieldHint}>Auto-preenchido pelo NCM do item</small>
                        </div>
                        <div className={styles.inputGroup}>
                            <label>ICMS (R$)</label>
                            <input
                                type="number"
                                step="0.00001"
                                value={icmsValue}
                                onChange={(e) => setIcmsValue(e.target.value)}
                                className={styles.input}
                                placeholder="0,00000"
                                disabled={formLoading}
                            />
                            <small className={styles.fieldHint}>Auto-preenchido pelo NCM do item</small>
                        </div>
                        <div className={styles.inputGroup}>
                            <label>ICMS ST (R$)</label>
                            <input
                                type="number"
                                step="0.00001"
                                value={icmsStValue}
                                onChange={(e) => setIcmsStValue(e.target.value)}
                                className={styles.input}
                                placeholder="0,00000"
                                disabled={formLoading}
                            />
                            <small className={styles.fieldHint}>Auto-preenchido pelo NCM do item</small>
                        </div>
                        <div className={styles.inputGroup}>
                            <label>PIS (R$)</label>
                            <input
                                type="number"
                                step="0.00001"
                                value={pisValue}
                                onChange={(e) => setPisValue(e.target.value)}
                                className={styles.input}
                                placeholder="0,00000"
                                disabled={formLoading}
                            />
                        </div>
                        <div className={styles.inputGroup}>
                            <label>COFINS (R$)</label>
                            <input
                                type="number"
                                step="0.00001"
                                value={cofinsValue}
                                onChange={(e) => setCofinsValue(e.target.value)}
                                className={styles.input}
                                placeholder="0,00000"
                                disabled={formLoading}
                            />
                        </div>
                        <div className={styles.inputGroup}>
                            <label>II (R$)</label>
                            <input
                                type="number"
                                step="0.00001"
                                value={iiValue}
                                onChange={(e) => setIiValue(e.target.value)}
                                className={styles.input}
                                placeholder="0,00000"
                                disabled={formLoading}
                            />
                        </div>
                        <div className={styles.inputGroup}>
                            <label>Frete (R$)</label>
                            <input
                                type="number"
                                step="0.01"
                                value={freightValue}
                                onChange={(e) => setFreightValue(e.target.value)}
                                className={styles.input}
                                placeholder="0,00"
                                disabled={formLoading}
                            />
                        </div>
                    </div>
                </div>

                {/* Desconto */}
                <div className={styles.formSection}>
                    <div className={styles.sectionHeader}>
                        <div className={styles.sectionIcon}><FiTag size={18} /></div>
                        <h3 className={styles.sectionTitle}>Desconto</h3>
                    </div>
                    <div className={styles.formGrid}>
                        <div className={styles.inputGroup}>
                            <label>Valor do desconto (%)</label>
                            <input
                                type="number"
                                step="0.01"
                                value={discountPercent}
                                onChange={(e) => setDiscountPercent(e.target.value)}
                                className={styles.input}
                                placeholder="0,00"
                                disabled={formLoading}
                            />
                        </div>
                    </div>
                </div>

                {/* Entrega */}
                <div className={styles.formSection}>
                    <div className={styles.sectionHeader}>
                        <div className={styles.sectionIcon}><FiTruck size={18} /></div>
                        <h3 className={styles.sectionTitle}>Entrega</h3>
                    </div>
                    <div className={styles.formGrid}>
                        <div className={styles.inputGroup}>
                            <label>Tipo de Entrega</label>
                            <select
                                value={deliveryType}
                                onChange={(e) => setDeliveryType(e.target.value)}
                                className={styles.select}
                                disabled={formLoading}
                            >
                                {DELIVERY_TYPES.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                        </div>
                        <div className={styles.inputGroup}>
                            <label>Frete por conta</label>
                            <select
                                value={freightBy}
                                onChange={(e) => setFreightBy(e.target.value)}
                                className={styles.select}
                                disabled={formLoading}
                            >
                                {FREIGHT_BY_OPTIONS.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                        </div>
                        <div className={styles.inputGroup}>
                            <label>Transportadora</label>
                            <select
                                value={selectedCarrierId}
                                onChange={(e) => setSelectedCarrierId(e.target.value)}
                                className={styles.select}
                                disabled={formLoading || carriersLoading}
                            >
                                <option value="">Selecione uma transportadora...</option>
                                {carrierOptions.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                            {carriersLoading && <small className={styles.fieldHint}>Carregando transportadoras...</small>}
                        </div>
                        <div className={styles.inputGroup}>
                            <label>Prazo Faturamento</label>
                            <select
                                value={selectedBillingConditionId}
                                onChange={(e) => setSelectedBillingConditionId(e.target.value)}
                                className={styles.select}
                                disabled={formLoading || billingConditionsLoading}
                            >
                                <option value="">Selecione uma condição...</option>
                                {billingConditionOptions.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                            {paymentDeadline && selectedBillingCondition && (
                                <small className={styles.successHint}>
                                    <FiCheck size={12} style={{ marginRight: '4px' }} />
                                    ✓ Dias: {paymentDeadline}
                                </small>
                            )}
                            {billingConditionsLoading && <small className={styles.fieldHint}>Carregando condições...</small>}
                        </div>
                    </div>
                </div>

                {/* Resumo de Valores */}
                <div className={styles.formSection}>
                    <div className={styles.sectionHeader}>
                        <div className={styles.sectionIcon}><FiTrendingUp size={18} /></div>
                        <h3 className={styles.sectionTitle}>Resumo de Valores</h3>
                    </div>
                    <div className={styles.resumoGrid}>
                        <div className={styles.resumoLinha}>
                            <span className={styles.resumoLabel}>Subtotal:</span>
                            <span className={styles.resumoValor}>R$ {parseFloat(calculated.subtotal).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                        </div>
                        {parseFloat(discountPercent) > 0 && (
                            <div className={styles.resumoLinha}>
                                <span className={styles.resumoLabel}>Desconto ({discountPercent}%):</span>
                                <span className={styles.resumoValor}>- R$ {parseFloat(calculated.discountAmount).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                            </div>
                        )}
                        <div className={styles.resumoLinha}>
                            <span className={styles.resumoLabel}>IPI:</span>
                            <span className={styles.resumoValor}>R$ {parseFloat(calculated.ipi).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                        </div>
                        <div className={styles.resumoLinha}>
                            <span className={styles.resumoLabel}>ICMS:</span>
                            <span className={styles.resumoValor}>R$ {parseFloat(calculated.icms).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                        </div>
                        <div className={styles.resumoLinha}>
                            <span className={styles.resumoLabel}>ICMS ST:</span>
                            <span className={styles.resumoValor}>R$ {parseFloat(calculated.icmsSt).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                        </div>
                        <div className={styles.resumoLinha}>
                            <span className={styles.resumoLabel}>PIS:</span>
                            <span className={styles.resumoValor}>R$ {parseFloat(calculated.pis).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                        </div>
                        <div className={styles.resumoLinha}>
                            <span className={styles.resumoLabel}>COFINS:</span>
                            <span className={styles.resumoValor}>R$ {parseFloat(calculated.cofins).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                        </div>
                        <div className={styles.resumoLinha}>
                            <span className={styles.resumoLabel}>Frete:</span>
                            <span className={styles.resumoValor}>R$ {parseFloat(calculated.freight).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                        </div>
                        <div className={`${styles.resumoLinha} ${styles.resumoTotal}`}>
                            <span className={styles.resumoLabel}>Valor Total:</span>
                            <span className={styles.resumoValorTotal}>R$ {parseFloat(calculated.total).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const renderStepReview = () => {
        const calculated = calculateValues;

        return (
            <div className={styles.stepContent}>
                <div className={styles.formSection}>
                    <div className={styles.sectionHeader}>
                        <div className={styles.sectionIcon}><FiCheckCircle size={18} style={{ color: 'var(--success-color)' }} /></div>
                        <h3 className={styles.sectionTitle}>Revisão do Pedido</h3>
                        <span className={styles.sectionBadge}>Confirme os dados</span>
                    </div>

                    <div className={styles.reviewGrid}>
                        <div className={styles.reviewItem}>
                            <span className={styles.reviewLabel}>Código:</span>
                            <span className={styles.reviewValue}>{orderCode}</span>
                        </div>
                        <div className={styles.reviewItem}>
                            <span className={styles.reviewLabel}>Fornecedor:</span>
                            <span className={styles.reviewValue}>{selectedSupplier?.razaoSocial || '-'}</span>
                        </div>
                        <div className={styles.reviewItem}>
                            <span className={styles.reviewLabel}>Data:</span>
                            <span className={styles.reviewValue}>{orderDate}</span>
                        </div>
                        <div className={styles.reviewItem}>
                            <span className={styles.reviewLabel}>Itens:</span>
                            <span className={styles.reviewValue}>{orderItems.length}</span>
                        </div>
                        <div className={styles.reviewItem}>
                            <span className={styles.reviewLabel}>Subtotal:</span>
                            <span className={styles.reviewValue}>R$ {parseFloat(calculated.subtotal).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                        </div>
                        {parseFloat(discountPercent) > 0 && (
                            <div className={styles.reviewItem}>
                                <span className={styles.reviewLabel}>Desconto ({discountPercent}%):</span>
                                <span className={styles.reviewValue}>- R$ {parseFloat(calculated.discountAmount).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                            </div>
                        )}
                        <div className={styles.reviewItem}>
                            <span className={styles.reviewLabel}>Dólar ({dollarType}):</span>
                            <span className={styles.reviewValue}>R$ {parseFloat(dollarValue).toFixed(4)}</span>
                        </div>
                        <div className={styles.reviewItem}>
                            <span className={styles.reviewLabel}>Valor Net (USD):</span>
                            <span className={styles.reviewValue}>{valorNetUsd}</span>
                        </div>
                        <div className={styles.reviewItem}>
                            <span className={styles.reviewLabel}>Transportadora:</span>
                            <span className={styles.reviewValue}>{selectedCarrier?.razaoSocial || '-'}</span>
                        </div>
                        <div className={styles.reviewItem}>
                            <span className={styles.reviewLabel}>Condição Pagamento:</span>
                            <span className={styles.reviewValue}>{selectedBillingCondition?.name || '-'}</span>
                        </div>
                        <div className={styles.reviewItem} style={{ gridColumn: 'span 2', borderTop: '2px solid var(--orders-primary-color)', paddingTop: '12px' }}>
                            <span className={styles.reviewLabel} style={{ fontSize: '1.1rem', fontWeight: '700' }}>TOTAL:</span>
                            <span className={styles.reviewValue} style={{ fontSize: '1.3rem', fontWeight: '700', color: 'var(--orders-primary-color)' }}>
                                R$ {parseFloat(calculated.total).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                            </span>
                        </div>
                    </div>

                    {/* Observações */}
                    <div className={styles.inputGroup} style={{ marginTop: '16px', padding: '0 20px' }}>
                        <label>Observações Comerciais</label>
                        <textarea
                            value={commercialNotes}
                            onChange={(e) => setCommercialNotes(e.target.value)}
                            className={styles.textarea}
                            rows={3}
                            placeholder="Observações adicionais sobre o pedido..."
                            disabled={formLoading}
                        />
                    </div>

                    {/* Opções */}
                    <div style={{ display: 'flex', gap: '24px', marginTop: '16px', flexWrap: 'wrap', padding: '0 20px 16px' }}>
                        <label className={styles.checkboxLabel}>
                            <input
                                type="checkbox"
                                checked={sendToReceiving}
                                onChange={(e) => setSendToReceiving(e.target.checked)}
                                disabled={formLoading}
                            />
                            <span>Enviar para Recebimento</span>
                        </label>
                        <label className={styles.checkboxLabel}>
                            <input
                                type="checkbox"
                                checked={sendEmail}
                                onChange={(e) => setSendEmail(e.target.checked)}
                                disabled={formLoading}
                            />
                            <span>Enviar email</span>
                        </label>
                    </div>
                </div>
            </div>
        );
    };

    // ============================================
    // 3.11 RENDERIZAÇÃO DO MODAL
    // ============================================

    const renderOrderModal = () => {
        if (!isModalOpen) return null;

        return (
            <div className={styles.modalBackdrop}>
                <div className={styles.formContainer} style={{ maxWidth: '1400px' }}>
                    <div className={styles.modalHeader}>
                        <div>
                            <h2>{editingOrder ? `Editar Pedido` : `Novo Pedido de Compra`}</h2>
                            {editingOrder && (
                                <p className={styles.modalSubtitle}>
                                    Editando: <strong>{editingOrder.code}</strong>
                                </p>
                            )}
                        </div>
                        <button
                            type="button"
                            onClick={resetForm}
                            className={styles.modalCloseButton}
                            disabled={formLoading}
                        >
                            <FiX size={24} />
                        </button>
                    </div>

                    {formStatus.message && (
                        <div className={`${styles.alert} ${styles[formStatus.type]}`}>
                            <span className={styles.alertIcon}>
                                {formStatus.type === 'error' && <FiAlertCircle size={18} />}
                                {formStatus.type === 'success' && <FiCheck size={18} />}
                                {formStatus.type === 'info' && <FiInfo size={18} />}
                            </span>
                            {formStatus.message}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className={styles.formContent}>
                        {renderStepperHeader()}

                        {currentStep === 1 && renderStepSupplier()}
                        {currentStep === 2 && renderStepItemsAndDollar()}
                        {currentStep === 3 && renderStepTaxAndDelivery()}
                        {currentStep === 4 && renderStepReview()}

                        {renderStepperActions()}
                    </form>
                </div>
            </div>
        );
    };

    // ============================================
    // 3.12 RENDERIZAÇÃO PRINCIPAL
    // ============================================

    return (
        <div className={styles.pageContainer}>
            {/* Sistema de Toast - Using global Toast */}
            {toasts.length > 0 && (
                <div className={styles.toastContainer} aria-live="polite">
                    {toasts.map(toast => (
                        <Toast
                            key={toast.id}
                            message={toast.message}
                            type={toast.type}
                            onClose={() => setToasts(prev => prev.filter(t => t.id !== toast.id))}
                        />
                    ))}
                </div>
            )}

            {/* Delete Modal - Replaced ConfirmDialog */}
            {deleteModal && deleteModal.isOpen && (
                <DeleteModal
                    isOpen={deleteModal.isOpen}
                    title={deleteModal.title}
                    message={deleteModal.message}
                    itemName={deleteModal.itemName}
                    itemCode={deleteModal.itemCode}
                    confirmText={deleteModal.confirmText}
                    cancelText="Cancelar"
                    onConfirm={handleDeleteConfirm}
                    onCancel={handleDeleteCancel}
                    isLoading={formLoading}
                    icon={<FiTrash2 size={24} style={{ color: '#dc3545' }} />}
                />
            )}

            {/* Modals - Using global components */}
            {viewingOrder && (
                <ViewOrderModal
                    order={viewingOrder}
                    onClose={() => setViewingOrder(null)}
                />
            )}

            {trackingOrder && (
                <TrackingModal
                    order={trackingOrder}
                    onClose={() => setTrackingOrder(null)}
                    onUpdateStatus={async (orderId, status, description) => {
                        // Handle status update if needed
                        addToast(`Status atualizado para ${status}`, 'Success');
                    }}
                />
            )}

            {renderOrderModal()}

            {/* Header - Using global PageHeader */}
            <PageHeader
                title="Pedidos de Compra"
                actionText={can.create ? "Novo Pedido" : ""}
                onAction={handleNewClick}
                disabled={formLoading}
                showAction={can.create}
            />

            {/* Search Bar - Using global SearchBar */}
            <div className={styles.searchBar}>
                <SearchBar
                    placeholder="Código do pedido, fornecedor..."
                    onSearch={(value) => {
                        setSearchTerm(value);
                        setCurrentPage(1);
                    }}
                    initialValue={searchTerm}
                    debounceDelay={300}
                    disabled={formLoading}
                />
            </div>

            {/* Table */}
            <div className={styles.tableContainer}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th>Código</th>
                            <th>Fornecedor</th>
                            <th>Data</th>
                            <th>Status</th>
                            <th style={{ width: '280px' }}>Ações</th>
                        </tr>
                    </thead>
                    <tbody>
                        {showSkeleton ? (
                            <tr>
                                <td colSpan="5">
                                    <TableSkeleton columns={5} rows={ITEMS_PER_PAGE} />
                                </td>
                            </tr>
                        ) : currentOrders && currentOrders.length > 0 ? (
                            currentOrders.map(order => (
                                <tr key={order.id} className={styles.tableRow}>
                                    <td><strong>{order.code}</strong></td>
                                    <td>{order.supplierName}</td>
                                    <td>{order.orderDate}</td>
                                    <td>
                                        <StatusBadge status={order.status} />
                                    </td>
                                    <td>
                                        <div className={styles.actionsContainer}>
                                            {can.view && (
                                                <button
                                                    onClick={() => handleViewClick(order)}
                                                    className={`${styles.actionButton} ${styles.viewButton}`}
                                                    disabled={formLoading}
                                                    title="Visualizar"
                                                >
                                                    <FiEye size={14} />
                                                </button>
                                            )}
                                            {can.track && (
                                                <button
                                                    onClick={() => handleTrackClick(order)}
                                                    className={`${styles.actionButton} ${styles.trackButton}`}
                                                    disabled={formLoading}
                                                    title="Rastrear"
                                                >
                                                    <FiMapPin size={14} />
                                                </button>
                                            )}
                                            {can.pay && (
                                                <button
                                                    onClick={() => handleEarlyPaymentClick(order)}
                                                    className={`${styles.actionButton} ${styles.paymentButton}`}
                                                    disabled={formLoading || order.status === ORDER_STATUS.COMPLETED}
                                                    title="Pagamento Antecipado"
                                                >
                                                    <FiDollarSign size={14} />
                                                </button>
                                            )}
                                            {can.edit && (
                                                <button
                                                    onClick={() => handleEditClick(order)}
                                                    className={`${styles.actionButton} ${styles.editButton}`}
                                                    disabled={formLoading}
                                                    title="Editar"
                                                >
                                                    <FiEdit size={14} />
                                                </button>
                                            )}
                                            {can.delete && (
                                                <button
                                                    onClick={() => handleDelete(order)}
                                                    className={`${styles.actionButton} ${styles.deleteButton}`}
                                                    disabled={formLoading}
                                                    title="Excluir"
                                                >
                                                    <FiTrash2 size={14} />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="5">
                                    <EmptyState
                                        icon="📋"
                                        title={searchTerm ? 'Nenhum pedido encontrado com o termo buscado.' : 'Nenhum pedido cadastrado.'}
                                        message={searchTerm ? 'Tente buscar por outro termo.' : 'Comece criando um novo pedido de compra.'}
                                    />
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination - Using global Pagination */}
            {!showSkeleton && totalPages > 1 && (
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalItems={filteredOrders.length}
                    onPageChange={handlePageChange}
                    disabled={formLoading}
                    itemsLabel="pedidos"
                />
            )}
        </div>
    );
};

export default PurchaseOrderPage;